/**
 * Mobile HUD on top of the shared PlayScene: start hint, the buttons of the call to action, personal
 * best and the top 5 of all phones (server), pause when the app goes to the background. The end of a round (result → story → call to action)
 * is shown by the game itself; a tap skips to the next step. Also owns the input (touch anywhere / space
 * bar = flap).
 */
import Phaser from 'phaser';
import { CAMPAIGN, campaignEnding, openCta } from '../../../client-shared/campaign.js';
import {
  CSS,
  PALETTE,
  SceneKey,
  UI_FONT,
  UI_HEAVY,
  WorldScale,
  drawButton,
  drawPanel,
  formatEuro,
  type EndingStep,
  type PlayScene,
  type PlayState,
  type RunResult,
} from '../../../game-core/src/index.js';
import type { MobileScoreResponse } from '../../../shared/protocol.js';
import type { BestScore } from '../bestScore.js';
import type { GlobalScores } from '../globalScores.js';
import { MOBILE_RULES } from '../rules.js';

type Style = Phaser.Types.GameObjects.Text.TextStyle;

export class MobileHudScene extends Phaser.Scene {
  static readonly KEY = 'mobile-hud';

  private play!: PlayScene;
  private ws!: WorldScale;
  private hint!: Phaser.GameObjects.Text;
  private hintTween!: Phaser.Tweens.Tween;
  private panel!: Phaser.GameObjects.Container;
  private panelBest!: Phaser.GameObjects.Text;
  /** top 5 of all phones – empty until the server answered (or without a connection) */
  private panelBoard!: Phaser.GameObjects.Text;
  /** counts the lives – a late answer of the server must not end up on the next one */
  private run = 0;
  private panelBg!: Phaser.GameObjects.Graphics;
  private panelW = 0;
  /** [object, space after] – stacked top to bottom */
  private panelStack: [Phaser.GameObjects.Text | Phaser.GameObjects.Container, number][] = [];
  private againButton!: Phaser.GameObjects.Container;
  private restartAllowedAt = 0;
  private paused = false;
  /** call to action link – from the server (CTA_URL), empty when offline */
  private ctaUrl = '';

  constructor(
    private readonly bestScore: BestScore,
    private readonly globalScores: GlobalScores,
  ) {
    super(MobileHudScene.KEY);
  }

  create(): void {
    this.play = this.scene.get(SceneKey.Play) as PlayScene;
    fetch('/campaign.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((c: { ctaUrl?: string } | null) => (this.ctaUrl = c?.ctaUrl ?? ''))
      .catch(() => undefined);
    const { width: W, height: H } = this.scale;
    this.ws = new WorldScale(W, H);
    const px = (wu: number) => Math.round(this.ws.px(wu));
    const style = (size: number, color: string = CSS.text, bold = true): Style => ({
      fontFamily: UI_FONT,
      fontSize: `${px(size)}px`,
      fontStyle: bold ? 'bold' : 'normal',
      color,
      stroke: CSS.ink,
      strokeThickness: bold ? px(size * 0.16) : 0,
      align: 'center',
    });

    this.hint = this.add
      .text(W / 2, H * 0.62, '', { ...style(42), fontStyle: UI_HEAVY, wordWrap: { width: W * 0.86 } })
      .setOrigin(0.5);
    this.hintTween = this.tweens.add({ targets: this.hint, alpha: 0.35, duration: 650, yoyo: true, repeat: -1 });

    // the end of a round is shown by the game (over the whole screen); the lower part stays free for the buttons
    this.play.setEnding(campaignEnding({ headline: CAMPAIGN.cta.headline }, { bottom: 0.36 }));

    // buttons of the call to action – items stacked top to bottom, at the bottom of the screen
    const panelW = Math.min(W * 0.9, px(560));
    this.panelW = panelW;
    const innerW = panelW - px(56);
    const text = (size: number, color: string, value: string, outlined = false, weight = 'bold') =>
      this.add
        .text(0, 0, value, { ...style(size, color, outlined), fontStyle: weight, wordWrap: { width: innerW, useAdvancedWrap: true } })
        .setOrigin(0.5, 0);
    this.panelBest = text(24, CSS.orangeLight, '');
    this.panelBoard = text(20, CSS.text, '', false, 'normal');
    const btnH = px(88);
    const ctaButton = this.button(innerW, btnH, CAMPAIGN.cta.button, PALETTE.orange, PALETTE.orangeDark, CSS.ink, () => openCta(this.ctaUrl));
    this.againButton = this.button(innerW, btnH, 'Nochmal spielen', PALETTE.blue, PALETTE.navy, CSS.text, () => this.restart());
    this.panelBg = this.add.graphics();
    this.panelStack = [
      [ctaButton, px(16)],
      [this.againButton, px(14)],
      [this.panelBest, px(10)],
      [this.panelBoard, 0],
    ];
    this.panel = this.add.container(W / 2, H, [this.panelBg, ...this.panelStack.map(([o]) => o)]).setVisible(false);

    // input – pointerdown fires on touchstart, no click delay
    this.input.on(Phaser.Input.Events.POINTER_DOWN, this.onTap, this);
    for (const key of ['SPACE', 'UP']) {
      this.input.keyboard?.on(`keydown-${key}`, (e: KeyboardEvent) => {
        if (e.repeat) return;
        if (this.play.state === 'OVER' && this.panel.visible) this.restart();
        else this.onTap();
      });
    }

    // app goes to the background → pause the run instead of letting the bird crash
    this.game.events.on(Phaser.Core.Events.HIDDEN, this.onHidden, this);

    this.play.signals.on('stateChange', this.onState, this);
    this.play.signals.on('runStart', this.onRunStart, this);
    this.play.signals.on('runEnd', this.onRunEnd, this);
    this.play.signals.on('ending', this.onEnding, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.play.signals.off('stateChange', this.onState, this);
      this.play.signals.off('runStart', this.onRunStart, this);
      this.play.signals.off('runEnd', this.onRunEnd, this);
      this.play.signals.off('ending', this.onEnding, this);
      this.game.events.off(Phaser.Core.Events.HIDDEN, this.onHidden, this);
    });

    this.restart();
  }

  private onTap(): void {
    if (this.paused) {
      this.paused = false;
      this.scene.resume(SceneKey.Play);
      this.onState(this.play.state);
      return;
    }
    switch (this.play.state) {
      case 'READY':
      case 'RUNNING':
        this.play.flap();
        break;
      case 'ATTRACT':
        this.restart();
        break;
      case 'OVER':
        // result and story: a tap skips ahead – then only the buttons act
        if (this.play.endingStep !== 'cta') this.play.skipEnding();
        break;
    }
  }

  private restart(): void {
    if (this.play.state === 'OVER' && performance.now() < this.restartAllowedAt) return;
    this.play.prepareRun({ lifeMs: MOBILE_RULES.lifeMs });
  }

  private onHidden(): void {
    if (this.play.state !== 'RUNNING' || this.paused) return;
    this.paused = true;
    this.scene.pause(SceneKey.Play);
    this.setHint('Pause – tippen zum Weiterspielen');
  }

  private onState(state: PlayState): void {
    switch (state) {
      case 'READY':
        this.panel.setVisible(false);
        this.setHint('Tippen zum Springen');
        break;
      case 'RUNNING':
      case 'OVER':
      case 'ATTRACT':
        this.setHint('');
        break;
    }
  }

  /** a life starts: the ticket for the highscore of all phones */
  private onRunStart(): void {
    this.run++;
    this.globalScores.startRound();
  }

  private onRunEnd(r: RunResult): void {
    const isNewBest = this.bestScore.submit(r.totalEur);
    this.panelBest.setText(isNewBest ? 'Neuer Rekord!' : `Rekord: ${formatEuro(this.bestScore.best)}`);
    this.panelBoard.setText('');
    this.layoutPanel();
    if (!this.play.endingStep) this.onEnding('cta');
    if (r.reason !== 'time') return;
    const run = this.run;
    void this.globalScores.finish(r.coins, r.specials).then((board) => {
      if (run !== this.run || this.play.state !== 'OVER' || !board) return;
      this.showBoard(board);
    });
  }

  /** the top 5 of all phones under the record – with the own place if it made it */
  private showBoard({ rank, entries }: MobileScoreResponse): void {
    if (!entries.length) return;
    const head = rank ? `Platz\u00a0${rank} der Handy-Highscore!` : 'Highscore aller Handys';
    const list = entries.map((e, i) => `${i + 1}.\u00a0${formatEuro(e.totalEur).replace(' ', '\u00a0')}`).join('  ·  ');
    this.panelBoard.setText(`${head}\n${list}`);
    this.layoutPanel();
  }

  /** the call to action of the end screen: now the buttons */
  private onEnding(step: EndingStep): void {
    if (step !== 'cta' || this.play.state !== 'OVER') return;
    this.restartAllowedAt = performance.now() + MOBILE_RULES.restartLockMs;
    this.againButton.setAlpha(0.4);
    this.panel.setVisible(true).setAlpha(0);
    this.tweens.add({ targets: this.panel, alpha: 1, duration: 250 });
    this.time.delayedCall(MOBILE_RULES.restartLockMs, () => this.againButton.setAlpha(1));
  }

  /** Stacks the panel items top to bottom (texts may wrap), the panel at the bottom of the screen. */
  private layoutPanel(): void {
    const pad = Math.round(this.ws.px(28));
    let y = pad;
    let lastAfter = 0;
    for (const [o, after] of this.panelStack) {
      // an empty text takes no space
      if (o instanceof Phaser.GameObjects.Text) o.setVisible(o.text !== '');
      if (!o.visible) continue;
      // texts have their origin at the top, buttons in the middle
      o.y = o instanceof Phaser.GameObjects.Text ? y : y + o.height / 2;
      y += o.height + after;
      lastAfter = after;
    }
    // no space after the last visible item
    y += pad - lastAfter;
    const px = (wu: number) => Math.round(this.ws.px(wu));
    this.panelBg.clear();
    drawPanel(this.panelBg, -this.panelW / 2, -y / 2, this.panelW, y, { radius: px(24), border: px(6), alpha: 0.94 });
    for (const [o] of this.panelStack) o.y -= y / 2;
    this.panel.y = this.scale.height - y / 2 - px(30);
  }

  /** Chunky pixel-style button. Acts on pointerup (counts as a user gesture for opening links). */
  private button(
    w: number,
    h: number,
    label: string,
    face: number,
    edge: number,
    color: string,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const px = (wu: number) => Math.round(this.ws.px(wu));
    const unit = Math.max(1, px(4));
    const text = this.add
      .text(0, -unit, label.toUpperCase(), {
        fontFamily: UI_FONT,
        fontSize: `${px(28)}px`,
        fontStyle: UI_HEAVY,
        color,
        align: 'center',
        wordWrap: { width: w - px(32) },
      })
      .setOrigin(0.5);
    // long labels make the button taller
    h = Math.max(h, text.height + px(36));
    const bg = this.add.graphics();
    drawButton(bg, w, h, face, edge, unit);
    const hit = this.add.zone(0, 0, w, h).setInteractive({ useHandCursor: true });
    const button = this.add.container(0, 0, [bg, text, hit]).setSize(w, h);
    hit.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => button.setScale(0.97));
    hit.on(Phaser.Input.Events.GAMEOBJECT_POINTER_OUT, () => button.setScale(1));
    hit.on(Phaser.Input.Events.GAMEOBJECT_POINTER_UP, () => {
      button.setScale(1);
      if (this.panel.visible && this.play.state === 'OVER') onClick();
    });
    return button;
  }

  private setHint(text: string): void {
    this.hint.setText(text).setVisible(text !== '');
    if (text) this.hintTween.restart();
  }
}
